import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Buildings, User, Image as ImageIcon, LinkSimple, X } from "@phosphor-icons/react";
import { unitTypeText } from "@/components/units/UnitLinks";

// Ansprechpartner aus dem Adressbuch: Personen, Firmen und deren Ansprechpartner
export function ContactPicker({ onPick, testPrefix = "mk-contact" }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) { setItems([]); return undefined; }
    const t = setTimeout(() => api.get("/marketing/contact-candidates", { params: { q } }).then(({ data }) => setItems(data || [])).catch(() => setItems([])), 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="relative">
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Aus Kontakten wählen (Person, Firma, Ansprechpartner)…" data-testid={`${testPrefix}-contact-search`} />
      {items.length > 0 && (
        <div className="absolute z-50 mt-1 w-full max-h-64 overflow-y-auto rounded-md border bg-white shadow-lg" data-testid={`${testPrefix}-contact-results`}>
          {items.map((c) => (
            <button key={`${c.contact_id}-${c.company_id || ""}`} type="button" onClick={() => { onPick(c); setQ(""); setItems([]); }}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-start gap-2 text-sm" data-testid={`${testPrefix}-contact-opt-${c.contact_id}`}>
              {c.is_company ? <Buildings size={16} className="mt-0.5 text-slate-500" /> : <User size={16} className="mt-0.5 text-slate-500" />}
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate">{c.name}{c.role ? <span className="text-slate-500 font-normal"> · {c.role}</span> : null}</div>
                <div className="text-[11px] text-slate-500 truncate">{[c.company_name && `bei ${c.company_name}`, c.email, c.phone].filter(Boolean).join(" · ")}</div>
              </div>
              {c.has_photo && <ImageIcon size={14} className="text-emerald-600 mt-1" title="Foto vorhanden" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Mehrere zusammengehörige Einheiten in einem Vorgang
export function ListingUnitsBundle({ listing, onChange }) {
  const [units, setUnits] = useState([]);
  const [add, setAdd] = useState("");
  useEffect(() => {
    if (listing.property_id) api.get("/properties/units/all", { params: { property_id: listing.property_id } }).then(({ data }) => setUnits(data || [])).catch(() => {});
  }, [listing.property_id]);
  if (!listing.unit_id) return null;
  const extra = listing.additional_unit_ids || [];
  const main = units.find((u) => u.id === listing.unit_id);
  const together = new Set((main?.linked_units || []).filter((l) => l.rule === "together").map((l) => l.unit_id));
  const save = async (ids, updateArea) => {
    try { const { data } = await api.put(`/marketing/listings/${listing.id}/units`, { additional_unit_ids: ids, update_area: updateArea }); onChange(data); toast.success("Einheiten aktualisiert"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen"); }
  };
  const free = units.filter((u) => u.id !== listing.unit_id && !extra.includes(u.id));
  const rows = (listing.bundle_units || []).length ? listing.bundle_units : [main, ...extra.map((id) => units.find((u) => u.id === id))].filter(Boolean);
  return (
    <div className="rounded-md border border-slate-200 p-3 space-y-2" data-testid="mk-bundle">
      <div className="text-xs font-semibold flex items-center gap-1"><LinkSimple size={13} /> Einheiten in diesem Vorgang</div>
      <p className="text-[11px] text-slate-500">Die Einheiten bleiben im System einzeln bestehen und werden diesem Vorgang zugeordnet.</p>
      {rows.map((u, i) => (
        <div key={u.id} className="flex items-center justify-between text-sm border rounded px-2 py-1" data-testid={`mk-bundle-unit-${u.id}`}>
          <span><span className="font-mono">{u.unit_number}</span> <span className="text-slate-500">· {unitTypeText(u)}{u.area || u.living_area || u.usable_area ? ` · ${u.area || u.living_area || u.usable_area} m²` : ""}</span>{i === 0 && <span className="ml-2 text-[10px] text-emerald-700">Haupteinheit</span>}</span>
          {i > 0 && (together.has(u.id)
            ? <span className="text-[10px] text-amber-700">nur gemeinsam</span>
            : <button type="button" className="text-slate-400 hover:text-rose-600" onClick={() => save(extra.filter((x) => x !== u.id), true)} data-testid={`mk-bundle-remove-${u.id}`}><X size={14} /></button>)}
        </div>
      ))}
      <div className="flex gap-2">
        <Select value={add || "__none"} onValueChange={(v) => setAdd(v === "__none" ? "" : v)}>
          <SelectTrigger className="h-9 flex-1" data-testid="mk-bundle-add-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none">Weitere Einheit hinzufügen…</SelectItem>
            {free.map((u) => <SelectItem key={u.id} value={u.id}>{u.unit_number} · {unitTypeText(u)}{u.location ? ` · ${u.location}` : ""}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button size="sm" variant="outline" disabled={!add} onClick={() => { save([...extra, add], true); setAdd(""); }} data-testid="mk-bundle-add">Hinzufügen</Button>
      </div>
    </div>
  );
}
